import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { Shield, Key, Truck, AlertTriangle } from "lucide-react";

const TYPES = [
  { value: "", label: "All" },
  { value: "verify", label: "Verifications" },
  { value: "deliver", label: "Deliveries" },
  { value: "steam_guard", label: "Steam Guard" },
  { value: "admin_login", label: "Admin Login" },
];

const iconFor = (type) => {
  switch (type) {
    case "verify": return Key;
    case "deliver": return Truck;
    case "steam_guard": return Shield;
    default: return AlertTriangle;
  }
};

export default function AdminLogs() {
  const { t } = useLang();
  const [type, setType] = useState("");
  const { data: logs = [] } = useQuery({
    queryKey: ["admin-logs", type],
    queryFn: async () => (await api.get(`/logs${type ? `?type=${type}` : ""}`)).data,
  });

  return (
    <div className="p-8" data-testid="admin-logs-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Activity</div>
          <h1 className="font-heading text-3xl font-bold">{t("admin.logs")}</h1>
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} className="bg-[#080808] border border-white/10 rounded px-3 py-2" data-testid="logs-type-filter">
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <div className="bg-[#0c0c0c] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-start px-4 py-3">Type</th>
              <th className="text-start px-4 py-3">Status</th>
              <th className="text-start px-4 py-3">Message</th>
              <th className="text-start px-4 py-3">IP</th>
              <th className="text-start px-4 py-3">Time</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => {
              const Icon = iconFor(l.type);
              return (
                <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5 text-zinc-500" />
                      <span className="text-xs">{l.type}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                      l.status === "success" ? "bg-brand-green/10 text-brand-green border border-brand-green/30" :
                      "bg-red-500/10 text-red-400 border border-red-500/30"
                    }`}>{l.status}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{l.message}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{l.ip || "—"}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                </tr>
              );
            })}
            {logs.length === 0 && (
              <tr><td colSpan={5} className="text-center py-12 text-zinc-500">{t("common.empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
